import React, { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import pckg from '../../../package.json'
import { groupSettings, settingsStructure, type SettingsItem, type SettingsStore } from './SettingsScturcture'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../../components/shadcn/Select'
import { Switch } from '../../components/shadcn/switch'
import { Button } from '../../components/shadcn/Button'
import { useSettings } from '../../contexts/SettingsContext'
import { useUnstructuredSettingsFlow } from './useUnstructuredSettingsFlow'
import { toastError } from '../../lib/toastHelpers'

export default function SettingsView() {
  const { store, set, reset } = useSettings()
  const {
    handleToggle: handleUnstructToggle,
    handlePicker: handleUnstructPicker,
    getPickerItems: getUnstructPickerItems,
    handleButton: handleUnstructButton,
    renderModals,
  } = useUnstructuredSettingsFlow()

  const [unstructPickerItems, setUnstructPickerItems] = useState<{ [id: string]: { label: string; value: string }[] }>(
    {},
  )

  useEffect(() => {
    ;(async () =>
      await Promise.all(
        settingsStructure.map(async item => {
          if (item.type === 'picker' && item.unstructFlowId) {
            const items = await getUnstructPickerItems(item.unstructFlowId)
            setUnstructPickerItems(prev => ({ ...prev, [item.unstructFlowId]: items }))
          }
        }),
      ))()
  }, [])

  // Request camera permission once when the settings page opens so that
  // the camera picker can immediately get the list of devices; after
  // permission is granted we refresh options for the camera picker.
  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) return
    ;(async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true })
        stream.getTracks().forEach(track => track.stop())

        const items = await getUnstructPickerItems('camera1')
        if (items) {
          setUnstructPickerItems(prev => ({ ...prev, camera1: items }))
        }
      } catch {
        // user denied or no camera
      }
    })()
  }, [getUnstructPickerItems])

  const environment = useMemo(() => window.depotoEnv || process.env.NODE_ENV || 'prod', [])

  const handleToggle = (key: keyof SettingsStore, value: boolean) => set(key, value)

  const handlePicker = (key: string, value: string) => set(key as keyof SettingsStore, value as any)

  const resetToDefaults = () => reset()

  const isDev = environment === 'dev' || process.env.NODE_ENV === 'development'
  const isDesktop = !!window._depoto

  // Group items by `group` field; fallback group "Obecné"
  const grouped = useMemo(() => {
    const map = new Map<string, SettingsItem[]>()
    for (const it of settingsStructure) {
      const grp = it.group || 'Obecné'
      const list = map.get(grp) || []
      list.push(it)
      map.set(grp, list)
    }
    return Array.from(map.entries()) as [string, SettingsItem[]][]
  }, [])

  const groups = useMemo(() => {
    return groupSettings.map(group => ({
      ...group,
      items: grouped.find(g => g[0] === group.name)?.[1] || [],
    }))
  }, [grouped])

  return (
    <div className="h-full flex flex-col">
      {renderModals()}
      <div className="flex-1 overflow-y-auto px-[30px] py-[20px] flex flex-col items-center">
        {groups.map(group => (
          <div key={group.name} className="mb-8 w-[60rem]">
            <div className="mb-3 flex items-center gap-6 w-full">
              <div className="text-2xl font-semibold flex items-center gap-4">
                <FontAwesomeIcon icon={group.icon} size="xs" />
                {group.name}
              </div>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
            <div className="grid grid-cols-1 gap-4 ml-4">
              {group.items.map((item: SettingsItem, idx) => {
                const disabled =
                  (Boolean(item.isDevLocked) && !isDev) ||
                  (item.isDesktopOnly && !isDesktop) ||
                  (item.isWebOnly && isDesktop)
                const description = item.description
                const icon = item.icon

                return (
                  <div key={idx} className="rounded-2xl border bg-white p-5 shadow-sm flex flex-row justify-between">
                    <div className="flex items-start gap-3">
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary ${
                          disabled ? 'opacity-50' : 'b'
                        }`}>
                        <FontAwesomeIcon icon={icon} size="lg" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-lg flex items-center gap-2">
                          <span>{item.label}</span>
                          {disabled && item.isWebOnly && (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                              Nastavení pro webovou verzi
                            </span>
                          )}
                          {disabled && item.isDesktopOnly && (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                              Nastavení pro desktop verzi
                            </span>
                          )}
                          {disabled && item.isDevLocked && (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                              Nastavení pro vývojáře
                            </span>
                          )}
                        </div>
                        {description && <div className="text-sm text-gray-500">{description}</div>}
                      </div>
                    </div>

                    {item.type === 'toggle' && (
                      <div className="flex items-center justify-between">
                        <Switch
                          checked={Boolean(store[item.storeKey] ?? false)}
                          onCheckedChange={checked => {
                            handleToggle(item.storeKey, Boolean(checked))
                            if (item.unstructFlowId) {
                              handleUnstructToggle(item.unstructFlowId, Boolean(checked))
                            }
                          }}
                          disabled={disabled}
                        />
                      </div>
                    )}

                    {item.type === 'picker' && (
                      <div className="flex items-center justify-between">
                        <div className="w-60">
                          <Select
                            value={String(store[item.storeKey] ?? '')}
                            onValueChange={val => {
                              handlePicker(String(item.storeKey), val)
                              if (item.unstructFlowId) {
                                handleUnstructPicker(item.unstructFlowId, val)
                              }
                            }}
                            disabled={disabled}>
                            <SelectTrigger>
                              <SelectValue placeholder="Vyberte možnost" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {item.unstructFlowId ? (
                                  (unstructPickerItems[item.unstructFlowId] || []).map(item => (
                                    <SelectItem key={item.value} value={item.value}>
                                      {item.label}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <>{item.unstructFlowId && <SelectLabel>Žádné možnosti</SelectLabel>}</>
                                )}
                                {!item.unstructFlowId && item.pickerOptions?.length ? (
                                  item.pickerOptions.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <>{!item.unstructFlowId && <SelectLabel>Žádné možnosti</SelectLabel>}</>
                                )}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}

                    {item.type === 'button' && (
                      <div className="flex justify-end">
                        <Button
                          disabled={disabled}
                          variant="outline"
                          onClick={() => {
                            if (item.buttonAction) {
                              item.buttonAction()
                            } else if (item.unstructFlowId) {
                              handleUnstructButton(item.unstructFlowId)
                            }
                          }}>
                          {item.buttonLabel || item.label}
                        </Button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between border-t border-gray-200 p-5 pl-10">
        <div className="flex flex-row gap-4 items-center">
          <div className="text-sm text-gray-500">Depoto Expedice</div>
          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
            v{String(pckg.version)}
          </span>
          <span className="rounded-full bg-light-blue px-3 py-1 text-xs font-medium text-green-700">
            Datum buildu: {__BUILD_DATE__}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="destructive" onClick={resetToDefaults}>
            Obnovit výchozí
          </Button>
        </div>
      </div>
    </div>
  )
}
